/**
 * <youtube-wall>
 *
 * A horizontal "video wall" of YouTube cards. Clicking a card opens a lightbox that
 * embeds and autoplays the video (no iframes load until clicked — fast). Arrow buttons
 * scroll the carousel. One shared lightbox is reused across all instances.
 */
class YoutubeWall extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-yt-track]');
    this.prev = this.querySelector('[data-yt-prev]');
    this.next = this.querySelector('[data-yt-next]');

    this.addEventListener('click', this.#onClick);

    if (this.track) {
      this.track.addEventListener('scroll', this.#updateArrows, { passive: true });
      window.addEventListener('resize', this.#updateArrows, { passive: true });
      // Initial state after layout settles.
      requestAnimationFrame(this.#updateArrows);
    }
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.#updateArrows);
  }

  #onClick = (event) => {
    const card = event.target.closest('.yt-card');
    if (card && this.contains(card)) {
      const id = card.dataset.ytId;
      if (id) YoutubeLightbox.open(id);
      return;
    }
    if (event.target.closest('[data-yt-prev]')) this.#scrollByCards(-1);
    if (event.target.closest('[data-yt-next]')) this.#scrollByCards(1);
  };

  #scrollByCards(direction) {
    if (!this.track) return;
    const card = this.track.querySelector('.yt-card');
    const step = card ? card.getBoundingClientRect().width + 18 : this.track.clientWidth * 0.8;
    this.track.scrollBy({ left: step * direction * 1.5, behavior: 'smooth' });
  }

  #updateArrows = () => {
    if (!this.track || !this.prev || !this.next) return;
    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    const max = scrollWidth - clientWidth - 2;
    this.prev.disabled = scrollLeft <= 2;
    this.next.disabled = scrollLeft >= max;
  };
}

/**
 * Shared lightbox for playing a YouTube video over the page.
 */
const YoutubeLightbox = {
  el: null,
  frame: null,
  lastFocused: null,

  open(id) {
    this._ensure();
    this.lastFocused = document.activeElement;
    this.frame.innerHTML =
      `<iframe src="https://www.youtube.com/embed/${encodeURIComponent(id)}?autoplay=1&rel=0&modestbranding=1&playsinline=1" ` +
      `title="YouTube video player" allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>`;
    document.body.appendChild(this.el);
    document.body.style.overflow = 'hidden';
    // Force reflow so the opacity transition runs.
    void this.el.offsetWidth;
    this.el.setAttribute('data-open', '');
    this.closeBtn.focus();
  },

  close() {
    if (!this.el || !this.el.isConnected) return;
    this.el.removeAttribute('data-open');
    this.frame.innerHTML = ''; // stop playback
    const el = this.el;
    setTimeout(() => {
      if (el.parentNode) el.parentNode.removeChild(el);
      document.body.style.overflow = '';
    }, 200);
    if (this.lastFocused && typeof this.lastFocused.focus === 'function') this.lastFocused.focus();
  },

  _ensure() {
    if (this.el) return;
    const overlay = document.createElement('div');
    overlay.className = 'yt-lightbox';
    overlay.innerHTML =
      '<div class="yt-lightbox__frame" role="dialog" aria-modal="true" aria-label="Video player">' +
      '<button type="button" class="yt-lightbox__close" aria-label="Close video">' +
      '<svg viewBox="0 0 24 24" width="22" height="22" fill="none"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>' +
      '</button></div>';
    this.el = overlay;
    this.frame = overlay.querySelector('.yt-lightbox__frame');
    this.closeBtn = overlay.querySelector('.yt-lightbox__close');

    this.closeBtn.addEventListener('click', () => this.close());
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) this.close();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.el && this.el.hasAttribute('data-open')) this.close();
    });
  },
};

if (!customElements.get('youtube-wall')) {
  customElements.define('youtube-wall', YoutubeWall);
}
